<template>
  <div class="cn-about-card" :class="[`cn-about-card--${color}`]">
    <div class="cn-about-card__image">
      <CdnImg
        v-if="img"
        :src="img"
        format="webp"
        :width="569"
        :height="478"
        loading="lazy"
      />
    </div>
    <div class="cn-about-card__content">
      <h3 class="cn-about-card__title" v-html="title" />
      <p class="cn-about-card__description" v-html="description" />
      <UiLink :to="link.url">
        <span>{{ link.text }}</span>
        <UiIcon name="arrow-right" />
      </UiLink>
      <UiParallelogram
        :color="figuresColor"
        :style="{
          top: '-35px',
          right: '50px',
        }"
      />
      <UiParallelogram
        :color="figuresColor"
        :style="{
          bottom: '-45px',
          left: '40%',
        }"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { CdnImg } from '@webplatform/cdn-image';
import UiParallelogram from '@/components/ui/UiParallelogram';
import UiLink from '@/components/ui/UiLink';
import UiIcon from '@/components/ui/UiIcon';

type Colors = 'gray' | 'red' | 'yellow' | 'blue';
interface Props {
  title?: string;
  description?: string;
  color?: Colors;
  img?: string;
  link?: {
    text?: string;
    url?: string;
  };
}

const figuresColors: Record<Colors, string> = {
  gray: 'yellow',
  red: 'white',
  yellow: 'white',
  blue: 'aqua',
};

const props = withDefaults(defineProps<Props>(), {
  title: '',
  description: '',
  color: 'gray',
  img: '/',
  link: () => ({
    text: 'Link',
    url: '#',
  }),
});

const figuresColor = computed(() => figuresColors[props.color]);
</script>

<style lang="scss">
.cn-about-card {
  --ab-canyon-about-card-content-color-bg: var(--ab-canyon-color-neutral-light);
  --ab-canyon-about-card-content-color-text: var(--ab-canyon-color-dark);
  --ab-canyon-about-card-content-description-color-text: var(
    --ab-canyon-color-neutral
  );
  --ab-canyon-about-card-content-color-bg-gray: var(
    --ab-canyon-color-neutral-light
  );
  --ab-canyon-about-card-content-color-text-gray: var(--ab-canyon-color-dark);
  --ab-canyon-about-card-content-color-bg-red: var(--ab-canyon-color-accent);
  --ab-canyon-about-card-content-color-text-red: var(--ab-canyon-color-light);
  --ab-canyon-about-card-content-color-bg-yellow: var(
    --ab-canyon-color-accent-2
  );
  --ab-canyon-about-card-content-color-text-yellow: var(--ab-canyon-color-dark);
  --ab-canyon-about-card-content-color-bg-blue: var(--ab-canyon-color-primary);
  --ab-canyon-about-card-content-color-text-blue: var(--ab-canyon-color-light);

  &__image {
    img {
      object-fit: cover;
      width: 100%;
      aspect-ratio: 1.2 / 1;
    }
  }
  &__content {
    background-color: var(--ab-canyon-about-card-content-color-bg);
    color: var(--ab-canyon-about-card-content-color-text);
    padding-top: var(--size-4xl);
    padding-bottom: var(--size-4xl);
    padding-left: var(--size-xl);
    padding-right: var(--size-xl);
    overflow: hidden;
    position: relative;
    @media (min-width: 992px) {
      padding-left: var(--size-4xl);
      padding-right: var(--size-4xl);
    }
    > a {
      letter-spacing: 1px;
      column-gap: calc(var(--size-base) / 4);
      align-items: center;
      display: inline-flex;
      margin-top: var(--size-xl);
      &:hover {
        .cn-icon {
          transform: translateX(calc(var(--size-base) / 4));
        }
      }
      .cn-icon {
        transition-property: transform;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        transition-duration: 150ms;
        flex-shrink: 0;
        width: var(--size-base);
        height: var(--size-base);
      }
    }
    .cn-parallelogram {
      position: absolute;
      width: 4rem;
      height: 4rem;
    }
  }
  &__title {
    text-transform: uppercase;
    font-weight: 700;
    font-size: var(--text-lg-font-size);
    line-height: var(--text-lg-font-height);
    @media (min-width: 656px) {
      font-size: 1.25rem;
      line-height: var(--text-lg-font-height);
    }
  }
  &__description {
    max-width: 410px;
    margin-top: var(--size-base);
    color: color-mix(
      in srgb,
      var(--ab-canyon-about-card-content-color-text) 90%,
      transparent
    );
  }
  &--gray {
    --ab-canyon-about-card-content-color-bg: var(
      --ab-canyon-about-card-content-color-bg-gray
    );
    --ab-canyon-about-card-content-color-text: var(
      --ab-canyon-about-card-content-color-text-gray
    );
  }
  &--red {
    --ab-canyon-about-card-content-color-bg: var(
      --ab-canyon-about-card-content-color-bg-red
    );
    --ab-canyon-about-card-content-color-text: var(
      --ab-canyon-about-card-content-color-text-red
    );
  }
  &--yellow {
    --ab-canyon-about-card-content-color-bg: var(
      --ab-canyon-about-card-content-color-bg-yellow
    );
    --ab-canyon-about-card-content-color-text: var(
      --ab-canyon-about-card-content-color-text-yellow
    );
  }
  &--blue {
    --ab-canyon-about-card-content-color-bg: var(
      --ab-canyon-about-card-content-color-bg-blue
    );
    --ab-canyon-about-card-content-color-text: var(
      --ab-canyon-about-card-content-color-text-blue
    );
  }
}
</style>
